import React, { useState, useCallback, useMemo, useEffect } from "react";
import { v4 as uuidv4 } from "uuid";
import TextField from "@mui/material/TextField";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import Fade from "@mui/material/Fade";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import { alpha, styled } from "@mui/material/styles";
import { isEmpty } from "lodash";
import NotesDialog from "./NotesDialog";

const NoteInputField = styled((props) => <TextField {...props} />)(
  ({ theme }) => ({
    "& .MuiInputBase-root": {
      borderRadius: 3,
      position: "relative",
      backgroundColor: theme.palette.mode === "light" ? "#00000029" : "#1A2027",
      border: "none !important",
      fontSize: 11,
      width: "auto",
      padding: "3px !important",
      transition: theme.transitions.create([
        "border-color",
        "background-color",
        "box-shadow",
      ]),
      // Use the system font instead of the default Roboto font.
      fontFamily: [
        "-apple-system",
        "BlinkMacSystemFont",
        "Segoe UI",
        "Roboto",
        "Helvetica Neue",
        "Arial",
        "sans-serif",
        "Apple Color Emoji",
        "Segoe UI Emoji",
        "Segoe UI Symbol",
      ].join(","),

      "& .MuiSvgIcon-root": {
        fontSize: "19px",
      },
      "&:focus-within": {
        boxShadow: `${alpha(theme.palette.primary.main, 0.15)} 0 0 0 0.1rem`,
        borderColor: theme.palette.primary.main,
      },
    },
    "& .MuiInputBase-input": {
      padding: "0px 50px 0px 0px !important",
    },
    "& .MuiAutocomplete-endAdornment": {
      "& button:first-of-type": {
        "& .MuiSvgIcon-root:first-of-type": {
          marginTop: "2px",
          fontSize: "16px",
        },
      },
    },
  })
);

const filter = createFilterOptions({
  trim: true,
  matchFrom: "start",
});

export const MAX_CHAR_LENGTH = 5000;

const truncateCss = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const optionCss = {
  display: "block",
  fontSize: "14px",
  ...truncateCss,
};

export default React.memo(function AutoCompleteInput(props) {
  const {
    label = "",
    pogNumber,
    showAddOption = false,
    noteSaveType = "",
    onNoteTypeSaveChange = () => {},
    addOptionRenderer = () => <></>,
    onDialogClose = () => {},
    optionList = [],
    loading = false,
    value: defaultValue = null,
    updateList,
    onSelect,
    sx,
    ...rest
  } = props;
  const [list, setList] = useState([]);
  const [value, setValue] = useState(defaultValue);
  const [open, toggleOpen] = useState(false);
  const [dialogValue, setDialogValue] = useState({
    title: "",
  });

  useEffect(() => {
    setList(optionList);
  }, [optionList]);

  useEffect(() => {
    setValue(defaultValue);
  }, [defaultValue]);

  useEffect(() => {
    // Setting initial value only
    let currentValue = !!value ? value : "";
    currentValue =
      typeof currentValue === "number" ? { id: currentValue } : currentValue;
    if (list.length && !isEmpty(currentValue)) {
      const isPresent = list.find((option) => option?.id === currentValue?.id);
      currentValue = isPresent ? isPresent : "";
    }
    setValue(currentValue);
  }, []);

  const handleClose = () => {
    setDialogValue({
      title: "",
    });
    toggleOpen(false);
    onDialogClose?.();
  };

  const handleSubmit = async (event) => {
    const userInput = dialogValue.title.trim();
    handleClose();
    onOptionChange(event, { title: userInput });
    const currentSelection = list.find((note) => note.title === userInput);
    onSelect?.(event, currentSelection);
    await updateList?.(dialogValue);
    event.stopPropagation();
  };

  const getFilterOptions = useCallback(
    (options, params) => {
      const filtered = filter(options, params) ?? [];
      if (showAddOption) {
        let isSame = !!filtered.length;
        if (!isSame) {
          const isFound =
            list.find((item) => item.title === params.inputValue.trim()) ??
            null;
          isSame = !!isFound;
        }
        if (params.inputValue !== "" && !isSame) {
          filtered.push({
            inputValue: params.inputValue,
            title: params.inputValue,
          });
        }
      }
      return filtered;
    },
    [showAddOption, list]
  );

  const getOptionLabel = useCallback((option) => {
    if (typeof option === "string") {
      return option;
    }
    if (option.inputValue) {
      return option.inputValue;
    }
    return option.title || "";
  }, []);

  const onOptionChange = useCallback(
    (event, newValue, reason = "") => {
      if (reason === "clear") {
        onSelect?.(event, "");
      }
      if (typeof newValue === "string") {
        toggleOpen(true);
        setDialogValue({
          title: newValue,
        });
      } else if (newValue && newValue.inputValue) {
        toggleOpen(true);
        setDialogValue({
          title: newValue.inputValue,
        });
      } else {
        setValue(newValue);
        onSelect?.(event, newValue);
      }
    },
    [onSelect, setDialogValue, setValue, toggleOpen]
  );

  const optionRenderer = useCallback(
    (props, option) => (
      <li {...props} key={`${option?.id}-${uuidv4()}`}>
        <span
          title={typeof option?.title === "string" ? option.title : ""}
          style={optionCss}
        >
          {option?.inputValue
            ? addOptionRenderer(option?.title)
            : option?.title}
        </span>
      </li>
    ),
    [addOptionRenderer]
  );

  const inputRenderer = useCallback(
    (params) => {
      let val =
        params.inputProps.value === "undefined" ? "" : params.inputProps.value;

      return (
        <Box sx={{ position: "relative" }}>
          <NoteInputField
            {...params}
            label={label}
            inputProps={{
              ...params?.inputProps,
              value: val,
              maxLength: MAX_CHAR_LENGTH,
            }}
            disabled={loading}
          />
          {loading && (
            <Fade in>
              <LinearProgress
                sx={{
                  position: "absolute",
                  bottom: "1px",
                  borderBottomRightRadius: "2px",
                  borderBottomLeftRadius: "2px",
                  left: 1,
                  right: 1,
                }}
              />
            </Fade>
          )}
        </Box>
      );
    },
    [loading, label]
  );

  const dialogInputChange = useCallback(
    (event) =>
      setDialogValue({
        title: event.target.value,
      }),
    []
  );

  const memoizedValue = useMemo(() => {
    if (!!value) {
      let currentValue = ["string", "number"].includes(typeof value)
        ? { id: value }
        : value;
      return currentValue;
    }
    return "";
  }, [value]);

  const getOptionEqualToValue = useCallback(
    (option, _value) => {
      return (
        option?.id === memoizedValue?.id ||
        option?.title === memoizedValue?.title
      );
    },
    [memoizedValue]
  );

  return (
    <>
      <Autocomplete
        autoComplete
        key={value || pogNumber}
        value={value || undefined}
        onChange={onOptionChange}
        filterOptions={getFilterOptions}
        options={list}
        getOptionLabel={getOptionLabel}
        renderOption={optionRenderer}
        sx={sx}
        renderInput={inputRenderer}
        isOptionEqualToValue={getOptionEqualToValue}
        disabled={loading}
        {...rest}
      />
      {dialogValue?.title && (
        <NotesDialog
          value={dialogValue.title}
          open={open}
          onClose={handleClose}
          handleSubmit={handleSubmit}
          dialogInputChange={dialogInputChange}
          noteSaveType={noteSaveType}
          onNoteTypeSaveChange={onNoteTypeSaveChange}
          list={list}
        />
      )}
    </>
  );
});
